import { useEffect, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import * as api from "../api/client";
import { formatGold, formatVoidJuice } from "../logic/currency";
import { pixelTheme } from "../theme/pixel";
import { defaultBtn, primaryBtn, withBtnStyle } from "../theme/styles";
import { inertAttrs, inertOnClick } from "../logic/inert";

// Same allowlist as LoginPage's `pattern` — keeps the two flows
// consistent so a name accepted by the guest convert path would also
// be accepted by a fresh signup.
function safeReturnTo(raw: string | null): string | null {
  if (!raw) return null;
  if (!raw.startsWith("/") || raw.startsWith("//")) return null;
  return raw;
}

type Reason = "packs" | "deck" | null;

function reasonCopy(reason: Reason): { headline: string; body: string } {
  switch (reason) {
    case "packs":
      return {
        headline: "Create an account to open packs.",
        body: "Your packs and XP are tracked while you play as a guest, but you'll need an account to actually open them.",
      };
    case "deck":
      return {
        headline: "Create an account to build decks.",
        body: "Guests play with the starter decks. Make an account to build and edit your own — your XP and packs will carry over.",
      };
    default:
      return {
        headline: "Create an account to save your progress.",
        body: "Your XP and packs carry over to your new account.",
      };
  }
}

export function ConvertAccountPage() {
  const theme = pixelTheme;
  const navigate = useNavigate();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const reason = (params.get("reason") as Reason) ?? null;
  const returnTo = safeReturnTo(params.get("returnTo"));
  const copy = reasonCopy(reason);

  const [user, setUser] = useState<api.User | null>(null);
  const [username, setUsername] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const MIN_PASSWORD = 10;

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const me = await api.getMe();
        if (cancelled) return;
        if (!me.user.is_guest) {
          // Already a full account — no conversion to do.
          navigate(returnTo ?? "/lobby", { replace: true });
          return;
        }
        setUser(me.user);
      } catch (err) {
        if (cancelled) return;
        if (err instanceof api.ApiError && err.status === 401) {
          navigate("/login", { replace: true });
          return;
        }
        setError(err instanceof Error ? err.message : String(err));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [navigate, returnTo]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    const name = username.trim();
    const mail = email.trim();
    if (!name || !mail || password.length < MIN_PASSWORD || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await api.convertGuest(name, mail, password);
      // The server cleared the guest session and is waiting for the
      // user to verify the new email before they can log in. Send
      // them to a "check your email" view.
      navigate(`/check-your-email?email=${encodeURIComponent(mail)}`, {
        replace: true,
      });
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setSubmitting(false);
    }
  }

  return (
    <div
      style={{
        minHeight: "100%",
        background: theme.pageBg,
        color: theme.ink,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        fontFamily: theme.fontBody,
        padding: 20,
      }}
    >
      <form
        onSubmit={onSubmit}
        style={{
          background: theme.panel,
          border: `2px solid ${theme.panelEdgeStrong}`,
          padding: 28,
          width: "100%",
          maxWidth: 460,
          display: "grid",
          gap: 18,
        }}
      >
        <div
          style={{
            fontFamily: theme.fontDisplay,
            fontSize: 14,
            letterSpacing: theme.letterSpacing,
            color: theme.accent,
            textAlign: "center",
          }}
        >
          {copy.headline}
        </div>
        <div style={{ fontSize: 14, color: theme.inkDim, textAlign: "center" }}>
          {copy.body}
        </div>
        {user && (
          <div
            style={{
              padding: 12,
              background: theme.bgDeep,
              border: `2px solid ${theme.panelEdge}`,
              display: "grid",
              gap: 6,
              fontSize: 13,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: theme.inkFaint }}>GUEST NAME</span>
              <span>{user.username}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: theme.inkFaint }}>VOID JUICE</span>
              <span>{formatVoidJuice(user.void_juice_ml)}</span>
            </div>
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: theme.inkFaint }}>GOLD</span>
              <span>{formatGold(user.gold_coins)}</span>
            </div>
          </div>
        )}
        <label
          style={{
            display: "grid",
            gap: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.inkDim,
          }}
        >
          NEW USERNAME
          <input
            autoFocus
            type="text"
            autoComplete="username"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
            disabled={submitting}
            placeholder="pick a name"
            pattern="[A-Za-z0-9_\-]{3,32}"
            title="3–32 characters: letters, numbers, underscores, hyphens"
            className="doi-input"
            style={{
              fontFamily: theme.fontBody,
              fontSize: 20,
              padding: "8px 10px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </label>
        <label
          style={{
            display: "grid",
            gap: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.inkDim,
          }}
        >
          EMAIL
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            placeholder="we'll send a verification link"
            className="doi-input"
            style={{
              fontFamily: theme.fontBody,
              fontSize: 20,
              padding: "8px 10px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </label>
        <label
          style={{
            display: "grid",
            gap: 6,
            fontFamily: theme.fontDisplay,
            fontSize: 11,
            letterSpacing: theme.letterSpacing,
            color: theme.inkDim,
          }}
        >
          PASSWORD
          <input
            type="password"
            autoComplete="new-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            placeholder={`at least ${MIN_PASSWORD} characters`}
            className="doi-input"
            style={{
              fontFamily: theme.fontBody,
              fontSize: 20,
              padding: "8px 10px",
              background: theme.bgDeep,
              color: theme.ink,
              border: `2px solid ${theme.panelEdge}`,
            }}
          />
        </label>
        {error && (
          <div style={{ color: theme.accent, fontSize: 14 }}>{error}</div>
        )}
        <button
          type="submit"
          {...inertAttrs(
            submitting ||
              username.trim().length < 3 ||
              email.trim().length === 0 ||
              password.length < MIN_PASSWORD,
          )}
          {...withBtnStyle(primaryBtn(theme, submitting), {
            fontSize: 12,
            padding: "10px 16px",
            cursor: submitting ? "wait" : undefined,
          })}
        >
          {submitting ? "…" : "CREATE ACCOUNT"}
        </button>
        <button
          type="button"
          onClick={inertOnClick(submitting, () => navigate(returnTo ?? "/lobby"))}
          {...inertAttrs(submitting)}
          {...withBtnStyle(defaultBtn(theme, submitting), {
            fontSize: 11,
            padding: "8px 14px",
          })}
        >
          NOT YET — KEEP PLAYING AS GUEST
        </button>
      </form>
    </div>
  );
}
